import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { X } from "lucide-react";
import { eWayBillRejectAction } from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import { parseIrpError } from "../e-invoice/create-e-invoice";

/* Confirmation popup to reject an e-way bill from the e-Way Bills list */
const RejectEWayBillModal = ({ soitId, ewbNo, transactionNo, onClose, onRejected }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const onFailed = (err) => {
    setSaving(false);
    const parsed = parseIrpError(err);
    setError(parsed);
    errorToast(parsed.details[0] ?? parsed.message ?? "Failed to reject e-Way Bill");
  };

  const handleConfirm = () => {
    setSaving(true);
    setError(null);
    dispatch(
      eWayBillRejectAction(
        { rejectItems: [{ soit_id: soitId, ewbNo: String(ewbNo) }] },
        (res) => {
          /* the bulk API can answer 200 with status false for a failed item */
          if (res?.status === false) {
            onFailed(res);
            return;
          }
          setSaving(false);
          successToast(res?.message ?? "e-Way Bill rejected successfully");
          onRejected?.();
        },
        onFailed
      )
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}>
      <div className="bg-white rounded-[12px] shadow-xl w-full max-w-[460px] mx-4" style={{ fontFamily: "Inter, sans-serif" }}>
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--light-border-color)]">
          <span className="font-[700] text-[var(--secondary)]" style={{ fontSize: "14px" }}>
            Reject e-Way Bill
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-[var(--placeholder-color)] hover:text-[var(--secondary)]"
          >
            <X size={18} />
          </button>
        </div>

        {/* body */}
        <div className="px-5 py-5 flex flex-col gap-3 text-[var(--secondary)]" style={{ fontSize: "13px" }}>
          <div>
            <span className="font-[600]">Transaction #: </span>
            {transactionNo || "—"}
          </div>
          <div>
            <span className="font-[600]">EWB #: </span>
            {ewbNo || "—"}
          </div>
          <div className="text-[var(--placeholder-color)]">
            Are you sure you want to reject this e-Way Bill? This cannot be undone.
          </div>

          {error && (
            <div className="rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-3 py-2 text-[#B42318]" style={{ fontSize: "12px" }}>
              <div className="font-[600]">{error.message}</div>
              {error.details?.length > 0 && (
                <ul className="list-disc pl-5">
                  {error.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-[var(--light-border-color)]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-[var(--draft-button-bg)] cursor-pointer
              text-[var(--draft-button-fg)]
              border border-[var(--draft-button-border)]
              text-[length:var(--draft-button-fs)]
              font-[var(--draft-button-fw)]"
          >
            Close
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleConfirm}
            className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md
              bg-F94B4B text-white border border-F94B4B
              font-[var(--primary-button-font-weight)]
              text-[length:var(--primary-button-create-fs)]
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Rejecting…" : "Confirm Reject"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RejectEWayBillModal;
